import styled, { keyframes } from 'styled-components';
import { Button, Tag, Input, Select, DatePicker, Table } from 'antd';
import { COLORS } from '../../constants/colors';

// Animation keyframes
const slideInFromTop = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInFromLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInScale = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

export const PageContainer = styled.div`
  min-height: 100vh;

  // Global animation classes
  .header-animate-in {
    animation: ${slideInFromTop} 0.6s ease-out forwards;
    opacity: 0;
  }

  .stats-animate-in {
    animation: ${fadeInScale} 0.6s ease-out forwards;
    animation-delay: 0.1s;
    opacity: 0;
  }

  .filters-animate-in {
    animation: ${slideInFromLeft} 0.6s ease-out forwards;
    animation-delay: 0.2s;
    opacity: 0;
  }

  .table-animate-in {
    animation: ${fadeInScale} 0.6s ease-out forwards;
    animation-delay: 0.3s;
    opacity: 0;
  }

  .table-animate-out {
    opacity: 0;
    transform: scale(0.95);
  }
`;

export const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 600;
  margin: 0;
  color: ${COLORS.textPrimary};
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

export const ActionButton = styled(Button)`
  border-radius: 8px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const FilterContainer = styled.div`
  margin-bottom: 24px;
`;

export const StyledInput = styled(Input)`
  height: 40px !important;
  border-radius: 10px;
  font-size: 14px;
  
  &:focus {
    border-color: ${COLORS.primary};
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }

  .ant-input-prefix {
    margin-right: 8px;
  }
`;

export const StyledSelect = styled(Select)`
  height: 40px !important;
  
  .ant-select-selector {
    height: 40px !important;
    border-radius: 8px !important;
    font-size: 14px;
    
    &:focus {
      border-color: ${COLORS.primary};
      box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
    }
  }
  
  .ant-select-selection-item {
    line-height: 38px !important;
    height: 38px !important;
    font-size: 14px !important;
  }
  
  .ant-select-selection-placeholder {
    line-height: 38px !important;
    height: 38px !important;
    font-size: 14px !important;
  }
`;

export const StyledRangePicker = styled(DatePicker.RangePicker)`
  height: 40px !important;
  border-radius: 8px;
  font-size: 14px;
  
  &:focus {
    border-color: ${COLORS.primary};
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }
  
  .ant-picker-input {
    height: 38px !important;
    line-height: 38px !important;
    font-size: 14px !important;
    
    input {
      height: 38px !important;
      line-height: 38px !important;
      font-size: 14px !important;
    }
  }
`;

export const StyledTable = styled(Table)`
  /* Match Event (Booking) table styling */
  animation-fill-mode: both;
  background: white;
  border-radius: 16px;
  border: 1px solid #f0f0f0;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);

  .ant-table {
    border-radius: 0;
    overflow: hidden;
    background: white;
    border: none;
  }

  .ant-table-container { overflow-x: auto; }

  .ant-table-thead > tr > th {
    background: white !important;
    color: ${COLORS.textDark || COLORS.textPrimary} !important;
    border-bottom: 3px solid ${COLORS.$primaryBlue || COLORS.primary} !important;
    font-weight: 600 !important;
    font-size: 13px !important;
    height: 52px !important;
    padding: 14px 20px !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
    position: relative;

    &:first-child { border-top-left-radius: 16px; }
    &:last-child { border-top-right-radius: 16px; }
  }

  .ant-table-tbody > tr {
    transition: all 0.2s ease;
    border-bottom: 1px solid #f5f5f5;

    &:hover > td { background: #fafbff !important; transform: translateY(-1px); }
    &:last-child { border-bottom: none; }
    &:nth-child(even) { background: #fafafa; &:hover > td { background: #f0f7ff !important; } }
  }

  .ant-table-tbody > tr > td {
    border-bottom: none;
    padding: 18px 20px;
    font-size: 14px;
    line-height: 1.5;
    vertical-align: middle;
    text-align: left;
    transition: all 0.2s ease;
  }

  /* Ensure timestamp column does not have a distinct gray background; inherit row */
  .ant-table-tbody > tr > td:nth-child(1) {
    background: transparent !important;
  }

  /* Pagination styling */
  .ant-pagination {
    margin: 0 !important;
    padding: 12px 16px !important;
    border-top: 1px solid ${COLORS.border};
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;

    .ant-pagination-total-text {
      font-size: 13px;
      color: ${COLORS.textMedium || COLORS.textSecondary};
      font-weight: 500;
      flex: 1;
    }

    .ant-pagination-options {
      display: flex;
      align-items: center;
      gap: 8px;
      height: 32px;
      
      .ant-pagination-options-size-changer {
        height: 32px !important;
        
        .ant-select-selector {
          border-radius: 16px !important;
          border: 1.5px solid ${COLORS.border} !important;
          height: 32px !important;
          min-height: 32px !important;
          min-width: 100px !important;
          transition: all 0.3s ease !important;
          display: flex !important;
          align-items: center !important;
          padding: 0 28px 0 12px !important;
          box-sizing: border-box !important;
          
          &:hover {
            border-color: ${COLORS.$primaryBlue || COLORS.primary} !important;
            box-shadow: 0 2px 8px rgba(24, 144, 255, 0.15) !important;
          }
        }
        
        .ant-select-selection-item {
          height: 28px !important;
          line-height: 28px !important;
          padding: 0 !important;
          display: flex !important;
          align-items: center !important;
          font-weight: 500 !important;
          font-size: 13px !important;
          flex: 1 !important;
          min-width: 0 !important;
        }
        
        .ant-select-selection-search {
          height: 28px !important;
          padding-left: 8px !important;
          
          .ant-select-selection-search-input {
            height: 28px !important;
            line-height: 28px !important;
          }
        }
        
        .ant-select-arrow {
          right: 10px !important;
          flex-shrink: 0 !important;
          height: 14px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          
          .anticon {
            line-height: 1 !important;
            vertical-align: middle !important;
          }
        }
        
        &.ant-select-focused .ant-select-selector {
          border-color: ${COLORS.$primaryBlue || COLORS.primary} !important;
          box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.15) !important;
        }
      }
      
      .ant-pagination-options-quick-jumper {
        display: flex;
        align-items: center;
        height: 32px;
        font-size: 13px;
        
        .ant-input {
          border-radius: 16px !important;
          border: 1.5px solid ${COLORS.border} !important;
          height: 32px !important;
          min-height: 32px !important;
          width: 50px !important;
          text-align: center;
          font-weight: 600;
          font-size: 13px;
          transition: all 0.3s ease !important;
          line-height: 28px !important;
          padding: 0 0 0 8px !important;
          
          &:hover {
            border-color: ${COLORS.$primaryBlue || COLORS.primary} !important;
            box-shadow: 0 2px 8px rgba(24, 144, 255, 0.15) !important;
          }
          
          &:focus {
            border-color: ${COLORS.$primaryBlue || COLORS.primary} !important;
            box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.15) !important;
          }
        }
      }
    }

    .ant-pagination-item {
      border-radius: 50% !important;
      border: 1.5px solid ${COLORS.border};
      min-width: 32px !important;
      height: 32px !important;
      line-height: 28px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
      
      a {
        color: ${COLORS.textDark || COLORS.textPrimary};
        font-weight: 600;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        text-decoration: none;
        font-size: 13px;
      }
      
      &:hover {
        border-color: ${COLORS.$primaryBlue || COLORS.primary};
        background: rgba(24, 144, 255, 0.1);
        transform: translateY(-1px);
        
        a {
          color: ${COLORS.$primaryBlue || COLORS.primary};
        }
      }
      
      &.ant-pagination-item-active {
        background: ${COLORS.$primaryBlue || COLORS.primary};
        border-color: ${COLORS.$primaryBlue || COLORS.primary};
        transform: translateY(-1px);
        
        a {
          color: white;
          font-weight: 700;
        }
        
        &:hover {
          background: ${COLORS.$primaryBlueDark || COLORS.primary};
          border-color: ${COLORS.$primaryBlueDark || COLORS.primary};
          
          a {
            color: white;
          }
        }
      }
    }

    .ant-pagination-prev,
    .ant-pagination-next {
      border-radius: 50% !important;
      border: 1.5px solid ${COLORS.border};
      min-width: 32px !important;
      height: 32px !important;
      line-height: 28px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
      
      .anticon {
        color: ${COLORS.textDark || COLORS.textPrimary};
        font-size: 14px;
      }
      
      &:hover {
        border-color: ${COLORS.$primaryBlue || COLORS.primary};
        background: rgba(24, 144, 255, 0.1);
        transform: translateY(-1px);
        
        .anticon {
          color: ${COLORS.$primaryBlue || COLORS.primary};
        }
      }
      
      &.ant-pagination-disabled {
        border-color: #e8e8e8;
        background: #f5f5f5;
        
        .anticon {
          color: ${COLORS.textMuted || '#bbb'};
        }
        
        &:hover {
          border-color: #e8e8e8;
          background: #f5f5f5;
          transform: none;
          
          .anticon {
            color: ${COLORS.textMuted || '#bbb'};
          }
        }
      }
    }

    .ant-pagination-jump-prev,
    .ant-pagination-jump-next {
      border-radius: 50% !important;
      border: 1.5px solid ${COLORS.border};
      min-width: 32px !important;
      height: 32px !important;
      line-height: 28px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
      
      .anticon {
        color: ${COLORS.textMuted || '#bbb'};
        font-size: 14px;
      }
      
      &:hover {
        border-color: ${COLORS.$primaryBlue || COLORS.primary};
        background: rgba(24, 144, 255, 0.1);
        transform: translateY(-1px);
        
        .anticon {
          color: ${COLORS.$primaryBlue || COLORS.primary};
        }
      }
    }
  }

  @media (max-width: 1200px) {
    .ant-table-thead > tr > th,
    .ant-table-tbody > tr > td { padding: 16px 12px; font-size: 13px; }
  }
  @media (max-width: 768px) {
    .ant-table { font-size: 12px; }
    .ant-table-thead > tr > th, .ant-table-tbody > tr > td { padding: 12px 8px; font-size: 12px; white-space: nowrap; }
    .ant-table-thead > tr > th { height: 44px !important; font-size: 11px !important; text-transform: uppercase; letter-spacing: 0.3px; }
  }
`;

export const StyledTableContainer = styled.div`
  /* Wrapper mimicking events table wrapper */
  background: transparent;
  padding: 0;
  width: 100%;
  box-sizing: border-box;
`;

export const PaginationText = styled.span`
  font-size: 14px;
  color: ${COLORS.textSecondary};
  font-weight: 500;
`;

export const AuditCountTag = styled.div`
  font-size: 14px;
  font-weight: 600;
  background-color: black;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 4px 12px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const ActionTag = styled(Tag)`
  border-radius: 6px;
  font-weight: 500;
  padding: 4px 8px;
  border: none;
  
  &.create {
    background: #f6ffed;
    color: #52c41a;
    border: 1px solid #b7eb8f;
  }
  
  &.update {
    background: #e6f7ff;
    color: #1890ff;
    border: 1px solid #91d5ff;
  }
  
  &.delete {
    background: #fff2f0;
    color: #ff4d4f;
    border: 1px solid #ffccc7;
  }
  
  &.view {
    background: #e6fffb;
    color: #13c2c2;
    border: 1px solid #87e8de;
  }
  
  &.match {
    background: #f9f0ff;
    color: #722ed1;
    border: 1px solid #d3adf7;
  }
  
  &.flag {
    background: #fff7e6;
    color: #fa8c16;
    border: 1px solid #ffd591;
  }
`;

export const ResourceTag = styled(Tag)`
  border-radius: 6px;
  font-weight: 500;
  background: ${COLORS.backgroundSecondary};
  color: ${COLORS.textPrimary};
  border: 1px solid ${COLORS.border};
  margin: 0;
`;

export const SummaryCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid ${COLORS.border};
  transition: all 0.3s ease;
  text-align: center;
  width: 100%;
  height: 100%;

  &:hover {
    border-color: ${COLORS.primary};
    box-shadow: 0 4px 16px rgba(24, 144, 255, 0.1);
    transform: translateY(-2px);
  }

  .statistic-value {
    font-size: 24px;
    font-weight: 700;
    color: ${COLORS.primary};
    margin-bottom: 4px;
  }

  .statistic-title {
    font-size: 14px;
    color: ${COLORS.textSecondary};
    font-weight: 500;
  }
`;
